/**
 * A1 — Calendario del mes (agenda). Deslizar = cambia de mes · tocar un día lo abre ·
 * punto dorado en días con citas · cerrados/bloqueados atenuados · semana lun–dom (es) / dom–sáb (en).
 * HOY nunca pierde su marca: seleccionado = relleno dorado sólido; hoy (no seleccionado) = borde dorado + «Hoy».
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { bookingApi } from '@/lib/bookingApi';
import { tr, localeTag } from '@/contexts/LanguageContext';
import { weekStartsMonday } from '@/lib/periodRange';

const pad = (n) => String(n).padStart(2, '0');
export const monthOf = (iso) => iso.slice(0, 7);
const shiftMonth = (ym, dir) => { const [y, m] = ym.split('-').map(Number); const d = new Date(Date.UTC(y, m - 1 + dir, 1)); return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`; };

export const DayCell = ({ iso, label, selected, isToday, closed, dots, muted, onPick, testid }) => {
  const base = 'relative flex flex-col items-center justify-center rounded-xl border transition-colors select-none';
  const tone = selected
    ? 'bg-primary border-primary text-primary-foreground shadow-[0_2px_10px_rgba(201,168,76,0.35)]'
    : isToday
      ? 'bg-background border-primary text-primary'
      : muted
        ? 'bg-background border-border/40 text-muted-foreground/50'
        : 'bg-background border-border text-foreground hover:border-primary/40';
  return (
    <button type="button" onClick={onPick} className={`${base} ${tone} ${closed && !selected ? 'opacity-40' : ''} h-12`}
            data-testid={testid} data-selected={selected ? 'true' : undefined} data-today={isToday ? 'true' : undefined} data-closed={closed || undefined} aria-pressed={selected}>
      <span className="font-display text-sm font-bold leading-none">{label}</span>
      {isToday ? (
        <span className="flex items-center gap-1 mt-0.5">
          <span className="text-[8px] uppercase tracking-wider font-semibold leading-none">{tr("Hoy")}</span>
          {dots && <span className={`w-1.5 h-1.5 rounded-full ${selected ? 'bg-primary-foreground' : 'bg-primary'}`} data-testid={`${testid}-dot`} />}
        </span>
      ) : dots ? (
        <span className={`w-1.5 h-1.5 rounded-full mt-1 ${selected ? 'bg-primary-foreground' : 'bg-primary'}`} data-testid={`${testid}-dot`} />
      ) : <span className="h-1.5 mt-1" />}
    </button>
  );
};

export const MonthCalendar = ({ barberRef, selected, today, onPick, initialMonth, canManage = true }) => {
  const [month, setMonth] = useState(initialMonth || monthOf(selected || today));
  const [data, setData] = useState({});
  const weekMon = weekStartsMonday();
  const touch = useRef(null);

  useEffect(() => {
    let alive = true;
    bookingApi.monthSummary(barberRef, month).then((r) => { if (alive) setData((p) => ({ ...p, [month]: r })); }).catch(() => {});
    return () => { alive = false; };
  }, [barberRef, month]);

  const cells = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    const first = new Date(Date.UTC(y, m - 1, 1));
    const lead = weekMon ? (first.getUTCDay() + 6) % 7 : first.getUTCDay();
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const out = Array.from({ length: lead }, () => null);
    for (let d = 1; d <= last; d += 1) out.push(`${month}-${pad(d)}`);
    while (out.length % 7) out.push(null);
    return out;
  }, [month, weekMon]);
  const info = useMemo(() => Object.fromEntries(((data[month] || {}).days || []).map((d) => [d.date, d])), [data, month]);
  const dow = useMemo(() => {
    const base = new Date(Date.UTC(2024, 0, weekMon ? 1 : 7)); // lunes 1-ene-2024 / domingo 7-ene-2024
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(base); d.setUTCDate(base.getUTCDate() + i); return d.toLocaleDateString(localeTag(), { weekday: 'narrow', timeZone: 'UTC' }); });
  }, [weekMon]);
  const title = new Date(`${month}-01T12:00:00`).toLocaleDateString(localeTag(), { month: 'long', year: 'numeric' });

  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touch.current === null) return;
    const dx = e.changedTouches[0].clientX - touch.current; touch.current = null;
    if (Math.abs(dx) > 50) setMonth((mm) => shiftMonth(mm, dx < 0 ? 1 : -1));
  };

  return (
    <div className="rounded-2xl bg-card border border-border p-3 space-y-2" data-testid="month-calendar" data-month={month}
         onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => setMonth((mm) => shiftMonth(mm, -1))} className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-primary/10" aria-label={tr("Mes anterior")} data-testid="month-calendar-prev"><ChevronLeft className="w-4 h-4" /></button>
        <p className="font-display text-sm font-bold text-foreground first-letter:uppercase" data-testid="month-calendar-title">{title}</p>
        <button type="button" onClick={() => setMonth((mm) => shiftMonth(mm, 1))} className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-primary/10" aria-label={tr("Mes siguiente")} data-testid="month-calendar-next"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {dow.map((d, i) => <span key={i} className="text-center text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{d}</span>)}
        {cells.map((iso, i) => iso ? (
          <DayCell key={iso} iso={iso} label={Number(iso.slice(8))} selected={iso === selected} isToday={iso === today}
                   closed={info[iso]?.closed} dots={canManage && (info[iso]?.bookings || 0) > 0} muted={iso < today}
                   onPick={() => onPick(iso)} testid={`month-day-${iso}`} />
        ) : <span key={`e${i}`} />)}
      </div>
      <p className="text-[10px] text-muted-foreground text-center" data-testid="month-calendar-hint">{tr("Desliza para cambiar de mes · toca un día para abrirlo")}</p>
    </div>
  );
};

export default MonthCalendar;
